import DownloadIcon from '@mui/icons-material/CloudDownloadOutlined';
import HomeIcon from '@mui/icons-material/Home';
import AddIcon from '@mui/icons-material/LibraryAddOutlined';
import {
    Breadcrumbs,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Link,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    TextField,
} from '@mui/material';
import { useOktaAuth } from '@okta/okta-react';
import { FunctionComponent, useEffect, useState } from 'react';
import Backend, { FileInfo } from '../backend/Backend';
import './StorageExplorer.scss';

import { useTranslation } from 'react-i18next';
import Util from '../Util';
import StorageFileType from '../storage/UserRoleType';

import FileTypeCsv from '../resources/images/csv.svg';
import FileTypeDoc from '../resources/images/doc.svg';
import FileTypeFolder from '../resources/images/folder.svg';
import FileTypePdf from '../resources/images/pdf.svg';
import FileTypePpt from '../resources/images/ppt.svg';
import FileTypeXls from '../resources/images/xls.svg';

const extension2IconMap = {
    csv: FileTypeCsv,
    pdf: FileTypePdf,
    xls: FileTypeXls,
    xlsx: FileTypeXls,
    doc: FileTypeDoc,
    docx: FileTypeDoc,
    ppt: FileTypePpt,
    pptx: FileTypePpt,
};

interface StorageExplorerProps {
    progress: Function;
    compact?: boolean;
    handleStorageFileSelected?: Function;
    allowedfilterType: StorageFileType;
}

type Path = {
    name: string;
    id: string;
};

const homeId = '____Home____';

const StorageExplorer: FunctionComponent<StorageExplorerProps> = (props: StorageExplorerProps) => {
    const { t } = useTranslation();
    const { authState, oktaAuth } = useOktaAuth();
    const { compact, allowedfilterType } = props;
    const currentTenant =
        Util.getSessionCurrentTenantInfo() && Util.getSessionCurrentTenantInfo().id
            ? Util.getSessionCurrentTenantInfo()
            : Util.getCurrentTenantInfo();
    const [path, setPath] = useState<Path[]>([{ id: homeId, name: currentTenant.name }]);
    const [items, setItems] = useState<Array<FileInfo>>([]);
    const [filteredItems, setFilteredItems] = useState<Array<FileInfo>>([]);
    const [openCreateFolderDialog, setOpenCreateFolderDialog] = useState<boolean>(false);
    const [newFolderName, setNewFolderName] = useState<string | null>(null);
    const [currentFileIndex, setCurrentFileIndex] = useState<number>(-1);
    const [fileFiterType, setFileFilterType] = useState<StorageFileType>(allowedfilterType);

    useEffect(() => {
        if (!authState || !authState.isAuthenticated) {
            return;
        }

        if (path.length === 1) {
            navigate(path);
        }
    }, [authState, oktaAuth]); // Update if authState changes

    const preparePath = (pathInfo: Path[]) => {
        let pathStr = '';

        // join the path items by a '/'
        const ids = [];
        for (const p in pathInfo) {
            if (pathInfo[p].id === homeId) {
                continue;
            }

            ids.push(pathInfo[p].name);
        }

        pathStr = ids.join('/');

        return pathStr;
    };

    const navigate = async (pathInfo: Path[]) => {
        props.progress(true);
        const pathStr = preparePath(pathInfo);

        const responseData = await Backend.listFolder(pathStr, oktaAuth);
        setItems(responseData.data.files);
        filterItems(responseData.data.files, fileFiterType);

        props.progress(false);
    };

    const handleItemClicked = async (f: FileInfo) => {
        if (f.is_folder) {
            const newPath = [...path];
            newPath.push({ id: f.id, name: f.name });
            setPath(newPath);

            navigate(newPath);
        } else {
            // this is a file
        }
    };

    const handleCreateFolderClicked = () => {
        setOpenCreateFolderDialog(true);
    };

    const createFolder = async (folderName: string) => {
        props.progress(true);
        const pathStr = preparePath(path);

        await Backend.createFolder(pathStr, folderName, oktaAuth);
        props.progress(false);

        handleDismissCreateFolderDialog();

        navigate(path);
    };

    const handleBreadcrumbClicked = async (index: number) => {
        const newPath = [];

        for (let i = 0; i <= index; i++) {
            newPath.push(path[i]);
        }

        setPath(newPath);
        navigate(newPath);
    };

    const handleDismissCreateFolderDialog = () => {
        setOpenCreateFolderDialog(false);
    };

    const handleDownloadFileClicked = async (f: FileInfo) => {
        const pathStr = preparePath(path);
        const sfiResponse = await Backend.downloadStorageFile(pathStr, f.id, oktaAuth);
        const downloadUrl = sfiResponse.data.file_name;
        window.open(downloadUrl, '_blank');
    };

    const handleFileSelectionChanged = async (e: any) => {
        props.progress(true);
        const pathStr = preparePath(path);

        await Backend.uploadFilesToStorage(e.target.files, pathStr, oktaAuth);
        props.progress(false);

        navigate(path);
    };

    const handleAddFileClicked = async (f: FileInfo) => {
        if (!props.handleStorageFileSelected) {
            return;
        }

        const pathStr = preparePath(path);
        props.handleStorageFileSelected(pathStr, f);
    };

    const handleFileFilterChanged = (e: SelectChangeEvent) => {
        const newFilterType = e.target.value as StorageFileType;
        setFileFilterType(newFilterType);
        filterItems(items, newFilterType);
    };

    const filterItems = (items: Array<FileInfo>, filterType: StorageFileType) => {
        const filterList = new Array<FileInfo>();
        for (const index in items) {
            if (items[index]) {
                const item = items[index];

                if (item.is_folder || filterType === StorageFileType.all) {
                    filterList.push({ ...item });
                    continue;
                }

                const fileExtension = item.name.split('.').pop().toLowerCase();
                if (fileExtension === filterType) {
                    filterList.push({ ...item });
                }

                // special cases
                if (
                    (fileExtension === 'pptx' && filterType === StorageFileType.ppt) ||
                    (fileExtension === 'xlsx' && filterType === StorageFileType.xls) ||
                    (fileExtension === 'docx' && filterType === StorageFileType.doc)
                ) {
                    filterList.push({ ...item });
                }
            }
        }

        setFilteredItems(filterList);
    };

    return (
        <div className="StorageExplorerRoot">
            <Stack direction="column" className={compact ? 'StorageExplorerCompact' : 'StorageExplorerNormal'}>
                <Stack direction="row" justifyContent="space-between">
                    <Breadcrumbs className="FilePath">
                        {path.map((folder, index) => (
                            <Link
                                key={index}
                                underline="hover"
                                sx={{ display: 'flex', alignItems: 'center' }}
                                color="inherit"
                                onClick={() => {
                                    handleBreadcrumbClicked(index);
                                }}
                            >
                                {folder.id === homeId && <HomeIcon sx={{ mr: 0.5 }} fontSize="medium" />}
                                {folder.name}
                            </Link>
                        ))}
                    </Breadcrumbs>
                    {!compact && (
                        <Stack direction="row" spacing={2}>
                            <Select
                                size="small"
                                value={fileFiterType}
                                onChange={handleFileFilterChanged}
                                style={{ width: 200 }}
                            >
                                <MenuItem value={StorageFileType.all}>{t('storage-explorer.all')}</MenuItem>
                                <MenuItem value={StorageFileType.csv}>{t('storage-explorer.csv')}</MenuItem>
                                <MenuItem value={StorageFileType.doc}>{t('storage-explorer.doc')}</MenuItem>
                                <MenuItem value={StorageFileType.pdf}>{t('storage-explorer.pdf')}</MenuItem>
                                <MenuItem value={StorageFileType.ppt}>{t('storage-explorer.ppt')}</MenuItem>
                                <MenuItem value={StorageFileType.xls}>{t('storage-explorer.xls')}</MenuItem>
                            </Select>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    handleCreateFolderClicked();
                                }}
                            >
                                {t('storage-explorer.create-folder')}
                            </Button>
                            <Button size="small" variant="contained" component="label">
                                {t('storage-explorer.upload-files')}
                                <input hidden accept="*/*" type="file" onChange={handleFileSelectionChanged} multiple />
                            </Button>
                        </Stack>
                    )}
                </Stack>

                <Stack direction="column" style={{ width: '100%' }}>
                    <Grid container className="StorageFileItem">
                        <Grid item xs="auto">
                            {!compact && <Checkbox />}
                            {compact ? <div className="StorageExplorerCheckboxPlaceHolder" /> : null}
                        </Grid>
                        <Grid item xs={6} className="StorageFileItemNameHeader">
                            {t('storage-explorer.folder-name')}
                        </Grid>
                        <Grid item xs={2} className="StorageFileItemHeader">
                            {t('storage-explorer.size')}
                        </Grid>
                        <Grid item xs={2} className="StorageFileItemHeader">
                            {t('storage-explorer.last-modified-at')}
                        </Grid>
                    </Grid>
                    <Grid container style={compact ? { maxHeight: 200, overflow: 'auto' } : {}}>
                        {filteredItems.map((f, index) => (
                            <Grid
                                key={index}
                                container
                                className={compact ? 'StorageFileItemCompact' : 'StorageFileItemNormal'}
                                onMouseEnter={() => {
                                    setCurrentFileIndex(index);
                                }}
                                onMouseLeave={() => {
                                    setCurrentFileIndex(-1);
                                }}
                            >
                                {!compact && (
                                    <Grid
                                        item
                                        xs="auto"
                                        className={
                                            compact ? 'StorageFileItemCheckCompact' : 'StorageFileItemCheckNormal'
                                        }
                                    >
                                        <Checkbox />
                                    </Grid>
                                )}
                                <Grid item xs={6} className="StorageFileItemName">
                                    <Button
                                        size={compact ? 'small' : 'medium'}
                                        startIcon={
                                            f.is_folder ? (
                                                <img alt="" style={{ width: 20 }} src={FileTypeFolder} />
                                            ) : (
                                                <img
                                                    alt=""
                                                    style={{ width: 20 }}
                                                    src={extension2IconMap[f.name.split('.').pop()]}
                                                />
                                            )
                                        }
                                        onClick={() => {
                                            handleItemClicked(f);
                                        }}
                                    >
                                        {f.name}
                                    </Button>

                                    {compact ? (
                                        <AddIcon
                                            fontSize="small"
                                            color="primary"
                                            className={
                                                currentFileIndex === index && !f.is_folder
                                                    ? 'DownloadFileVisible'
                                                    : 'DownloadFileHidden'
                                            }
                                            onClick={() => {
                                                handleAddFileClicked(f);
                                            }}
                                        />
                                    ) : null}

                                    {!compact && (
                                        <DownloadIcon
                                            color="primary"
                                            className={
                                                currentFileIndex === index && !f.is_folder
                                                    ? 'DownloadFileVisible'
                                                    : 'DownloadFileHidden'
                                            }
                                            onClick={() => {
                                                handleDownloadFileClicked(f);
                                            }}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={2} style={{ textAlign: 'end' }}>
                                    {f.is_folder
                                        ? ''
                                        : `${(f.size / 1024).toFixed(2)} ${t('storage-explorer.size-metric')}`}
                                </Grid>
                                <Grid item xs={2} style={{ textAlign: 'end' }}>
                                    {new Date(f.last_modified_at).toLocaleString()}
                                </Grid>
                            </Grid>
                        ))}
                    </Grid>
                </Stack>
            </Stack>

            <Dialog open={openCreateFolderDialog} onClose={handleDismissCreateFolderDialog}>
                <DialogTitle>{t('storage-explorer.title')}</DialogTitle>
                <DialogContent>
                    <TextField
                        margin="dense"
                        fullWidth
                        variant="standard"
                        placeholder={t('storage-explorer.folder-name-ph')}
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        required
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDismissCreateFolderDialog}>{t('common.cancel')}</Button>
                    <Button
                        disabled={!newFolderName || newFolderName.trim().length === 0}
                        onClick={() => {
                            createFolder(newFolderName);
                        }}
                    >
                        {t('storage-explorer.create-folder')}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default StorageExplorer;
